/**
 * /forgot-password — request a password-reset email code.
 *
 * Flow:
 *   signIn.create({ strategy: 'reset_password_email_code', identifier })
 *
 * Clerk sends the code immediately on `create`. The user then goes
 * to `/reset-password` to enter it. We show a confirmation screen
 * rather than auto-navigating so the user has a chance to copy the
 * email address they typed in case of typos.
 */

import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { requestPasswordReset } from '@/lib/auth';
import { useSignIn } from '@clerk/react/legacy';
import { CheckCircle } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_auth/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage(): ReactElement {
  const { signIn, isLoaded } = useSignIn();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | undefined>(undefined);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isLoaded || !signIn) return;

    setSubmitting(true);
    setEmailError(undefined);
    setFormError(undefined);

    const result = await requestPasswordReset({ signIn, email });

    if (result.kind === 'error') {
      setEmailError(result.fieldErrors.identifier ?? result.fieldErrors.emailAddress);
      setFormError(
        result.fieldErrors.identifier || result.fieldErrors.emailAddress
          ? undefined
          : result.formError,
      );
      setSubmitting(false);
      return;
    }

    setSent(true);
    setSubmitting(false);
  }

  if (sent) {
    return (
      <div className="space-y-4 text-center">
        <div className="flex justify-center text-[var(--color-success)]">
          <CheckCircle aria-hidden="true" size={40} weight="regular" />
        </div>
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Check your inbox</h1>
        <p className="text-sm text-[var(--color-mute)]">
          We sent a 6-digit code to{' '}
          <span className="font-medium text-[var(--color-ink)]">{email}</span>. Enter it on the next
          screen to reset your password.
        </p>
        <Button className="w-full" render={<Link to="/reset-password" />}>
          Enter code
        </Button>
        <p className="text-xs text-[var(--color-mute)]">
          Didn't get it?{' '}
          <button
            type="button"
            className="font-medium text-[var(--color-accent-brand)] hover:underline"
            onClick={() => setSent(false)}
          >
            Try a different email
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Forgot password?</h1>
        <p className="text-sm text-[var(--color-mute)]">
          Enter your email and we'll send you a code to reset it.
        </p>
      </header>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail((event.target as HTMLInputElement).value)}
            aria-invalid={emailError ? true : undefined}
          />
          {emailError && <FieldError>{emailError}</FieldError>}
        </Field>

        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send reset code'}
        </Button>
      </form>

      <p className="text-center text-sm text-[var(--color-mute)]">
        Remembered it?{' '}
        <Link
          to="/sign-in"
          className="font-medium text-[var(--color-accent-brand)] hover:underline"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}
